import {
  Modal,
  Box,
  TextField,
  Button,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from 'hooks/reduxHooks';
import { RootState } from 'redux-setup/store';
import { IRepository } from 'interface/repositoryInterface';
import { addRepoToOrganization } from 'services/repositoryService';
import { DEFAULT_YAML_PATH, DEFAULT_BRANCH } from './constant';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './style.scss';

interface IImportModalProps {
  repositories: IRepository[];
  fetchRepositories: () => void;
  fetchedRepositories: IRepository[];
}

export function ImportModal({
  repositories,
  fetchRepositories,
  fetchedRepositories,
}: IImportModalProps) {
  const organizationId = useAppSelector(
    (state: RootState) => state.organization.id,
  );

  const [expandedRepo, setExpandedRepo] = useState<string | false>(false);
  const [selectedRepos, setSelectedRepos] = useState<string[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [repoSettings, setRepoSettings] = useState<
    Record<
      string,
      {
        reliantYamlPath: string;
        mainBranch: string;
      }
    >
  >({});

  const handleNext = () => {
    setCurrentStep(2);
    const firstSelectedRepo = repositories?.find(repo =>
      selectedRepos.includes(repo.ID),
    );
    if (firstSelectedRepo) {
      setExpandedRepo(firstSelectedRepo.ID);
    }
  };

  const handleAccordionChange =
    (repoId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedRepo(isExpanded ? repoId : false);
    };

  const handleRepoToggle = (repoId: string) => {
    setSelectedRepos(prev =>
      prev.includes(repoId)
        ? prev.filter(id => id !== repoId)
        : [...prev, repoId],
    );
  };

  const handleSelectAll = () => {
    if (selectedRepos.length === repositories?.length) {
      setSelectedRepos([]);
    } else {
      setSelectedRepos(repositories?.map(repo => repo.ID) || []);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setCurrentStep(1);
    setSelectedRepos([]);
    setSearchTerm('');
  };

  const handleSettingChange = (
    repoId: string,
    setting: string,
    value: string,
  ) => {
    setRepoSettings(prev => ({
      ...prev,
      [repoId]: {
        ...(prev[repoId] || {
          reliantYamlPath: DEFAULT_YAML_PATH,
          mainBranch: DEFAULT_BRANCH,
        }),
        [setting]: value,
      },
    }));
  };

  const filteredRepos = repositories?.filter(repo => {
    const isImported = fetchedRepositories?.some(fetchedRepo => {
      return fetchedRepo.ExternalID === repo.ID;
    });
    return !isImported;
  });

  const handleImportRepositories = async () => {
    const selectedRepositories = repositories?.filter(repo =>
      selectedRepos.includes(repo.ID),
    );

    if (!selectedRepositories || !organizationId) {
      toast.error('Failed to import repositories');
      return;
    }

    let successCount = 0;
    let failureCount = 0;

    for (const repo of selectedRepositories) {
      try {
        const response = await addRepoToOrganization({
          OrganizationID: organizationId,
          GithubOwner: repo.OwnerName,
          GithubRepo: repo.RepoName,
          OwnerID: repo.OwnerID,
          Settings: {
            MainBranch: repoSettings[repo.ID]?.mainBranch || DEFAULT_BRANCH,
            ReliantYamlPath:
              repoSettings[repo.ID]?.reliantYamlPath || DEFAULT_YAML_PATH,
          },
        });

        if (response.status === 200) {
          successCount++;
        } else {
          failureCount++;
        }
      } catch (error) {
        failureCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`Successfully imported ${successCount} repositories`);
    }
    if (failureCount > 0) {
      toast.error(`Failed to import ${failureCount} repositories`);
    }

    handleClose();
  };

  const renderStepOne = () => (
    <>
      <h2>Select Repositories</h2>
      <div className="repo-modal-content">
        <div className="repo-modal-header">
          <TextField
            fullWidth
            placeholder="Search repositories..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <Button onClick={handleSelectAll}>
            {selectedRepos.length === repositories?.length
              ? 'Deselect All'
              : 'Select All'}
          </Button>
        </div>

        <div className="repo-list">
          {filteredRepos && filteredRepos.length > 0 ? (
            filteredRepos.map(repo => (
              <div key={repo.ID} className="repo-item">
                <Checkbox
                  checked={selectedRepos.includes(repo.ID)}
                  onChange={() => handleRepoToggle(repo.ID)}
                />
                <span>
                  {' '}
                  {repo.OwnerName} / {repo.RepoName}
                </span>
              </div>
            ))
          ) : (
            <div className="no-repos-message m-20">
              {searchTerm
                ? 'No repositories match your search'
                : 'No repositories available'}
            </div>
          )}
        </div>

        <div className="repo-modal-footer">
          <Button variant="outlined" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={selectedRepos.length === 0}
            onClick={handleNext}>
            Next
          </Button>
        </div>
      </div>
    </>
  );

  const renderStepTwo = () => (
    <>
      <h2>Configure Repositories</h2>
      <div className="repo-modal-content">
        <div className="selected-repos-list">
          {repositories
            ?.filter(repo => selectedRepos.includes(repo.ID))
            .map(repo => (
              <Accordion
                key={repo.ID}
                className="selected-repo-item"
                expanded={expandedRepo === repo.ID}
                onChange={handleAccordionChange(repo.ID)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>
                    {repo.OwnerName} / {repo.RepoName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <div className="repo-settings">
                    <TextField
                      label="Main Branch"
                      value={
                        repoSettings[repo.ID]?.mainBranch || DEFAULT_BRANCH
                      }
                      onChange={e =>
                        handleSettingChange(
                          repo.ID,
                          'mainBranch',
                          e.target.value,
                        )
                      }
                      size="small"
                      fullWidth
                      margin="normal"
                      placeholder={DEFAULT_BRANCH}
                    />
                    <TextField
                      label="Reliant YAML Path"
                      value={
                        repoSettings[repo.ID]?.reliantYamlPath ||
                        DEFAULT_YAML_PATH
                      }
                      onChange={e =>
                        handleSettingChange(
                          repo.ID,
                          'reliantYamlPath',
                          e.target.value,
                        )
                      }
                      size="small"
                      fullWidth
                      margin="normal"
                      placeholder={DEFAULT_YAML_PATH}
                      helperText="Path to the Reliant YAML configuration file"
                    />
                  </div>
                </AccordionDetails>
              </Accordion>
            ))}
        </div>

        <div className="repo-modal-footer">
          <Button variant="outlined" onClick={() => setCurrentStep(1)}>
            Previous
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleImportRepositories().then(() => {
                fetchRepositories();
              });
            }}>
            Import
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="import-repositories-container">
      <Button
        className="repo-settings"
        size="large"
        variant="outlined"
        onClick={() => {
          setIsModalOpen(true);
        }}>
        + Import Repositories
      </Button>

      <Modal
        open={isModalOpen}
        onClose={handleClose}
        aria-labelledby="repo-selection-modal">
        <Box className="repo-modal">
          {currentStep === 1 ? renderStepOne() : renderStepTwo()}
        </Box>
      </Modal>
    </div>
  );
}
